import React, { FC, useState, useEffect } from 'react';
import { Tabs } from '@udir-design/react/beta';
import { Field, Label, Select } from '@udir-design/react/beta';
import { MagnifyingGlassIcon, TasklistIcon, HatSchoolIcon, BarChartIcon, ParagraphIcon } from '@udir-design/icons';
import { DisplayFormat } from '../../../Models/DisplayFormat';
import { getCurrentDisplayFormat } from '../../../utils/generalHelpers';
import './SearchNavigationTabs.scss';

interface ISearchNavigationTabsProps {
    currentTab: 'global' | 'lareplan' | 'eksamen' | 'forskning' | 'regelverk';
    searchQuery?: string;
}

const SearchNavigationTabs: FC<ISearchNavigationTabsProps> = ({ currentTab, searchQuery }) => {
    const [displayFormat, setDisplayFormat] = useState<DisplayFormat>(getCurrentDisplayFormat());

    useEffect(() => {
        const handleResize = () => {
            setDisplayFormat(getCurrentDisplayFormat());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const tabMapping = {
        'global': 'MagnifyingGlass',
        'lareplan': 'TasklistIcon',
        'eksamen': 'HatSchoolIcon',
        'forskning': 'BarChartIcon',
        'regelverk': 'ParagraphIcon'
    };

    const tabLabels = {
        'MagnifyingGlass': 'Alt innhold',
        'TasklistIcon': 'Læreplan',
        'HatSchoolIcon': 'Eksamensoppgaver',
        'BarChartIcon': 'Forskning og statistikk',
        'ParagraphIcon': 'Regelverk'
    };

    const handleTabChange = (value: string) => {
        // Treat '*' as empty query — it's an internal wildcard, not a user-visible search term
        const sanitizedQuery = searchQuery && searchQuery.trim() !== '*' ? searchQuery : undefined;

        const urls = {
            'MagnifyingGlass': '/',
            'TasklistIcon': '/finn-lareplan.html',
            'HatSchoolIcon': '/eksamensoppgaver.html',
            'BarChartIcon': '/forskning-og-rapporter.html',
            'ParagraphIcon': '/regelverkstolkninger.html'
        };

        if (urls[value]) {
            let targetUrl = urls[value];
            
            if (value === 'TasklistIcon') {
                // When navigating to Læreplan, always add default filters
                const queryPart = sanitizedQuery ? `query=${encodeURIComponent(sanitizedQuery)}&` : '';
                targetUrl = `${targetUrl}?${queryPart}fltypefiltermulti=Læreplan&status=Gjeldende&filtervalues=all`;
            } else if (value === 'ParagraphIcon') {
                // When navigating to Regelverk, always add default filters
                const queryPart = sanitizedQuery ? `query=${encodeURIComponent(sanitizedQuery)}&` : '';
                targetUrl = `${targetUrl}?${queryPart}gyldighet=Gyldig innhold&filtervalues=all`;
            } else if (value === 'HatSchoolIcon') {
                // When navigating to Eksamensoppgaver, add filtervalues=all
                const queryPart = sanitizedQuery ? `query=${encodeURIComponent(sanitizedQuery)}&` : '';
                targetUrl = `${targetUrl}?${queryPart}filtervalues=all`;
            } else if (value === 'BarChartIcon') {
                // When navigating to Forskning og statistikk, add filtervalues=all
                const queryPart = sanitizedQuery ? `query=${encodeURIComponent(sanitizedQuery)}&` : '';
                targetUrl = `${targetUrl}?${queryPart}filtervalues=all`;
            } else if (sanitizedQuery) {
                // For other tabs
                targetUrl = `${targetUrl}?query=${encodeURIComponent(sanitizedQuery)}`;
            }
            
            window.location.href = targetUrl;
        }
    };

    const isDesktop = displayFormat === DisplayFormat.Desktop;

    if (isDesktop) {
        // Desktop: Use Tabs
        return (
            <Tabs className="navigationtabs-container" data-size='md' data-color="accent" defaultValue={tabMapping[currentTab]} onChange={handleTabChange}>
                <Tabs.List className="navigationtabs-list">
                    <Tabs.Tab value="MagnifyingGlass" >
                        <MagnifyingGlassIcon aria-hidden="true" />
                        <span className="navigationtabs-text">Alt innhold</span>
                    </Tabs.Tab>
                    <Tabs.Tab value="TasklistIcon" >
                        <TasklistIcon aria-hidden="true" />
                        <span className="navigationtabs-text">Læreplan</span>
                    </Tabs.Tab>
                    <Tabs.Tab value="HatSchoolIcon" >
                        <HatSchoolIcon aria-hidden="true" />
                        <span className="navigationtabs-text">Eksamensoppgaver</span>
                    </Tabs.Tab>
                    <Tabs.Tab value="BarChartIcon" >
                        <BarChartIcon aria-hidden="true" />
                        <span className="navigationtabs-text">Forskning og statistikk</span>
                    </Tabs.Tab>
                    <Tabs.Tab value="ParagraphIcon" >
                        <ParagraphIcon aria-hidden="true" />
                        <span className="navigationtabs-text">Regelverk</span>
                    </Tabs.Tab>
                </Tabs.List>
            </Tabs>
        );
    } else {
        // Mobile/Tablet: Use Select dropdown
        const currentValue = tabMapping[currentTab];
        return (
            <Field className="navigationfield" id="spesialsok-id">
                <Label className="navigationfield-label">Spesialsøk</Label>
                <Select className="navigationfield-select"
                        value={currentValue} 
                        onChange={(e) => handleTabChange(e.target.value)}
                >
                    <option className="navigationfield-value" value="MagnifyingGlass">{tabLabels['MagnifyingGlass']}</option>
                    <option className="navigationfield-value" value="TasklistIcon">{tabLabels['TasklistIcon']}</option>
                    <option className="navigationfield-value" value="HatSchoolIcon">{tabLabels['HatSchoolIcon']}</option>
                    <option className="navigationfield-value" value="BarChartIcon">{tabLabels['BarChartIcon']}</option>
                    <option className="navigationfield-value" value="ParagraphIcon">{tabLabels['ParagraphIcon']}</option>
                </Select>
            </Field>
        );
    }
};

export default SearchNavigationTabs;
